import type { ComponentType } from "react";

export type PrefetchableRoute =
  | "/chat"
  | "/meu-progresso"
  | "/profile";

type RouteModule = { default: ComponentType };
type RouteLoader = () => Promise<RouteModule>;

const started = new Map<PrefetchableRoute, Promise<RouteModule>>();

const shared = (route: PrefetchableRoute, loader: RouteLoader): Promise<RouteModule> => {
  const current = started.get(route);
  if (current) return current;

  const request = loader().catch((error) => {
    // Falha de rede/chunk não pode envenenar a rota para sempre. O boundary da
    // navegação continua dono da mensagem/retry; um próximo gesto tenta de novo.
    if (started.get(route) === request) started.delete(route);
    throw error;
  });
  started.set(route, request);
  return request;
};

export const loadChatRoute = () =>
  shared("/chat", () => import("@/pages/Chat") as Promise<RouteModule>);
export const loadProgressRoute = () =>
  shared("/meu-progresso", () => import("@/pages/meu-progresso/MeuProgresso") as Promise<RouteModule>);
export const loadProfileRoute = () =>
  shared("/profile", () => import("@/pages/Profile") as Promise<RouteModule>);

const loaders: Record<PrefetchableRoute, RouteLoader> = {
  "/chat": loadChatRoute,
  "/meu-progresso": loadProgressRoute,
  "/profile": loadProfileRoute,
};

/**
 * Começa a baixar/compilar o mesmo módulo que o React.lazy consumirá depois.
 * Prefetch é uma dica: erro fica silencioso aqui e reaparece somente se a rota
 * realmente for aberta, pelo boundary já existente.
 */
export const prefetchRoute = async (route: PrefetchableRoute): Promise<void> => {
  try {
    await loaders[route]();
  } catch {
    // O usuário ainda não navegou; não transforme especulação em erro de UI.
  }
};
